/* eslint-disable no-nested-ternary */
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { Tooltip } from "../utils/local-antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useTheme } from "../ThemeContext";
import { InputTypes } from "../model/form/types/InputTypes.enum";
import { AtiraIcon } from "./AtiraIcon";
import { Flex } from "./Flex";
import { InputProps } from "./Inputs/InputProps";
import { PasswordInputTypes } from "./Inputs/InputType.enum";
import { PhoneInputComponent } from "./Inputs/PhoneInput";
import { RegularDateInputComponent } from "./Inputs/RegularDateInput";
import { RegularInputComponent } from "./Inputs/RegularInput";
import { FontSize, Text } from "./Text";
import { LabelDirection } from "./shared/types/SharedLabelProps";

const StyledInfo = styled(Tooltip)`
  cursor: help;
`;

const OptionalSpan = styled.span`
  color: ${({ theme }) => theme.darkerSub};
  font-size: ${FontSize.s};
`;

export const Input: React.FC<InputProps> = (props) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  const { type, errorMessage, ...restProps } = props;

  const hasError = (errorMessage?.length || 0) > 0 && props.valid === false;

  const [inputType, setInputType] = useState(type);

  const togglePasswordInputType = () => {
    setInputType(
      inputType === PasswordInputTypes.PASSWORD.toLowerCase()
        ? PasswordInputTypes.TEXT.toLowerCase()
        : PasswordInputTypes.PASSWORD.toLowerCase(),
    );
  };

  return (
    <Flex flexDirection="column" gap="s" width={props.width || "100%"}>
      <Flex
        width={"100%"}
        gap="s"
        flexDirection={
          props.labelDirection === LabelDirection.HORIZONTAL ? "row" : "column"
        }
        className="atira-input-wrapper"
      >
        {props.title || props.maxLength || props.minLength ? (
          <Flex alignItems="center" justifyContent="space-between">
            {props.title ? (
              <Flex alignItems="center" justifyContent="flex-start" gap="s">
                {props?.info ? (
                  <StyledInfo title={props.info}>
                    <span>
                      <AtiraIcon icon={faInfoCircle} color="main" size="sm" />
                    </span>
                  </StyledInfo>
                ) : null}

                <label
                  htmlFor={props.id}
                  style={{
                    color: theme.black,
                    cursor: props?.id ? "pointer" : "auto",
                    textAlign: props.titleAlign,
                    width: "fit-content",
                    fontSize: FontSize.m,
                    alignSelf: props.titleAlign || "flex-start",
                    flex: props.labelTooltip ? "none" : 1,
                    margin:
                      props.labelDirection === LabelDirection.HORIZONTAL
                        ? "auto"
                        : "0",
                  }}
                >
                  {props.title}
                  {props.required ? (
                    <span style={{ color: theme.danger }}> *</span>
                  ) : (
                    <OptionalSpan> ({t("common.optional")})</OptionalSpan>
                  )}
                </label>
                {props.labelTooltip ? (
                  <Tooltip title={props.labelTooltip}>
                    <span>
                      <AtiraIcon icon={faExclamationCircle} color="lightMain" />
                    </span>
                  </Tooltip>
                ) : null}
              </Flex>
            ) : null}

            {props.maxLength || props.minLength ? (
              <Text
                fontSize="xs"
                color="darkerSub"
                style={{ opacity: props.disabled ? "0.2" : "1" }}
              >
                ({props.minLength ? `${props.minLength} - ` : null}
                {props.maxLength} {t("common.characters")})
              </Text>
            ) : null}
          </Flex>
        ) : null}

        {inputType === InputTypes.DATE.toLowerCase() ? (
          <RegularDateInputComponent {...props} />
        ) : inputType === InputTypes.PHONE.toLowerCase() ? (
          <PhoneInputComponent
            {...restProps}
            value={restProps.value as string}
            inputType={inputType}
            type={type}
            errorMessage={errorMessage}
          />
        ) : (
          <RegularInputComponent
            {...restProps}
            inputType={inputType}
            type={type}
            togglePasswordInputType={togglePasswordInputType}
            errorMessage={errorMessage}
          />
        )}

        {hasError ? (
          <Text color="danger" fontSize="s">
            {errorMessage}
          </Text>
        ) : null}
      </Flex>
    </Flex>
  );
};
